import { z } from 'zod'
import { BpmSchema, IdSchema } from './schema/v1'

/**
 * Memoria de práctica (§6, E2): al reabrir una canción recuperas el tempo al que la practicabas,
 * el último tempo cómodo, el fragmento que trabajabas, tus notas y su estado. No es parte de la
 * revisión: cambia en cada sesión y no debe crear revisiones (ADR 0003).
 */
export const PRACTICE_STATUSES = ['learning', 'polishing', 'mastered'] as const
export type PracticeStatus = (typeof PRACTICE_STATUSES)[number]

/** Un acorde de una aparición: los fragmentos apuntan a IDs, nunca a pulsos (regla 3). */
export const EventRefSchema = z.object({
  occurrence: IdSchema,
  chord: IdSchema,
})

/** Fragmento: del principio del acorde `from` al final del acorde `to`. */
export const FragmentSchema = z.object({
  from: EventRefSchema,
  to: EventRefSchema,
})

export const PracticeSchema = z.object({
  arrangementId: IdSchema,
  /** El tempo al que la practicas ahora. */
  practiceBpm: BpmSchema.optional(),
  /** El último tempo al que dijiste «me sale». */
  comfortableBpm: BpmSchema.optional(),
  status: z.enum(PRACTICE_STATUSES).optional(),
  notes: z.string().max(10000).optional(),
  fragment: FragmentSchema.optional(),
  updatedAt: z.iso.datetime(),
})

export type EventRef = z.infer<typeof EventRefSchema>
export type Fragment = z.infer<typeof FragmentSchema>
export type Practice = z.infer<typeof PracticeSchema>

export type PracticePatch = Partial<Omit<Practice, 'arrangementId' | 'updatedAt'>>

/**
 * Aplica cambios a la memoria de práctica. Un campo `undefined` en el parche lo borra; uno que no
 * está en el parche se queda como estaba.
 */
export function patchPractice(
  current: Practice | undefined,
  arrangementId: string,
  patch: PracticePatch,
  now: string,
): Practice {
  const next: Practice = { ...(current ?? { arrangementId }), arrangementId, updatedAt: now }
  for (const [key, value] of Object.entries(patch) as [keyof PracticePatch, unknown][]) {
    if (value === undefined) delete next[key]
    else (next as Record<string, unknown>)[key] = value
  }
  if (next.notes !== undefined && next.notes.trim() === '') delete next.notes
  return next
}
